import {
  formatter,
  MenuPriceFix,
  MenuPriceFixApplied,
  MenuPriceFixBlockedReason,
  MenuPriceFixCandidate,
} from '@vgrubs/components';

/**
 * Wording for the ticket's Menu price section. Every amount here is in CENTS,
 * unlike the dollars of `order.total` a few rows above it on the same screen.
 */

export const formatCents = (cents: number) => formatter.format(cents / 100);

/** What the merchant asked for, in one sentence. */
export const describePriceRequest = ({ kind, amount }: MenuPriceFix) => {
  if (kind === 'newPrice' && amount !== null) {
    return `Merchant says it should be ${formatCents(amount)}`;
  }
  // Signed: a positive change means the menu asks too little.
  if (kind === 'change' && amount) {
    return `Merchant says it is ${formatCents(Math.abs(amount))} too ${
      amount > 0 ? 'low' : 'high'
    }`;
  }
  return 'Merchant says the price is wrong, with no clear amount';
};

export const BLOCKED_REASON_TEXT: Record<MenuPriceFixBlockedReason, string> = {
  pos: 'Prices on this menu come from the POS.',
  providerPrices:
    'This dish has its own price per platform. Change it in the menu editor.',
};

/** "Wings on Wingz", always with the brand: one dish can sit on several. */
export const dishOnBrand = (dish: { itemName: string; brandName: string }) =>
  `${dish.itemName} on ${dish.brandName}`;

/** The current prices, under the dish's name. */
export const describeCurrentPrices = (candidate: MenuPriceFixCandidate) =>
  [
    `Delivery ${formatCents(candidate.price)}`,
    candidate.pickupPrice === null
      ? 'no pickup price'
      : `pickup ${formatCents(candidate.pickupPrice)}`,
  ].join(', ');

/**
 * The confirmation's line: "Wings on Wingz: delivery $10.00 → $15.00, pickup
 * $10.00 → $15.00". A pickup price that is not sent stays as it is, and says
 * so, so nobody approves thinking both moved.
 */
export const describePriceChange = (
  candidate: MenuPriceFixCandidate,
  price: number,
  pickupPrice: number | undefined
) => {
  const delivery = `delivery ${formatCents(candidate.price)} → ${formatCents(
    price
  )}`;
  const current = candidate.pickupPrice;
  const pickup =
    pickupPrice !== undefined
      ? `pickup ${
          current === null ? 'none' : formatCents(current)
        } → ${formatCents(pickupPrice)}`
      : current === null
      ? null
      : `pickup stays ${formatCents(current)}`;
  return `${dishOnBrand(candidate)}: ${[delivery, pickup]
    .filter(Boolean)
    .join(', ')}`;
};

/** An approved price as one history line. */
export const describeApplied = (applied: MenuPriceFixApplied) => {
  const pickupMoved =
    applied.newPickupPrice !== null &&
    applied.newPickupPrice !== applied.oldPickupPrice;
  const parts = [
    `${formatCents(applied.oldPrice)} → ${formatCents(applied.newPrice)}`,
    pickupMoved &&
      `pickup ${
        applied.oldPickupPrice === null
          ? 'none'
          : formatCents(applied.oldPickupPrice)
      } → ${formatCents(applied.newPickupPrice as number)}`,
    applied.published ? 'published' : 'not published',
  ];
  return `${dishOnBrand(applied)}: ${parts.filter(Boolean).join(', ')}`;
};
